import {
  Button,
  ButtonIconWithLabel,
  Icon_OpenNew24,
  Icon_TriangleDown,
  Menu,
  Spinner,
} from "@shapr3d/frontend-lib";

import { useDeepLinkStateStore } from "../../../stores/deepLinkStateStore";
import type { DisplayUser } from "../../../types";
import { TeamID } from "../../../types";
import { formatDate } from "../../../utils/helpers";
import { HeaderBreadCrumbs } from "../../HeaderBreadCrumbs/HeaderBreadCrumbs";
import { UserName } from "../../UserName/UserName";

import style from "./ProjectMetadata.module.css";

type ProjectMetadataProps = {
  teamId?: TeamID;
  spaceName?: string;
  projectName: string;
  lastModifiedAt?: Date;
  createdByUser?: DisplayUser;
  isLoading: boolean;
  showEditInAppButton: boolean;
  onMenuOpen?: () => void;
  onCopyLink?: () => void;
  onShare?: () => void;
  onDownload?: () => void;
  canShare?: boolean;
  canDownload?: boolean;
};

export const ProjectMetadata = ({
  teamId,
  spaceName,
  projectName,
  lastModifiedAt,
  createdByUser,
  isLoading,
  showEditInAppButton,
  onMenuOpen,
  onCopyLink,
  onShare,
  onDownload,
  canShare,
  canDownload,
}: ProjectMetadataProps) => {
  const { setIsModalOpen: setIsDeepLinkModalOpen } = useDeepLinkStateStore();

  if (isLoading) {
    return (
      <div className={style.loadingContainer}>
        <Spinner size={20} />
      </div>
    );
  }

  return (
    <div className={style.projectData}>
      {teamId && spaceName && (
        <div className={style.breadcrumbs}>
          <HeaderBreadCrumbs teamID={teamId} spaceName={spaceName} />
        </div>
      )}
      <Menu
        className={style.menuWrapper}
        activator={
          <div role="button" className={style.title} title={projectName}>
            <span className={style.titleText}>{projectName}</span>
            <Icon_TriangleDown className={style.triangleDown} />
          </div>
        }
        onOpen={onMenuOpen}
      >
        {showEditInAppButton && (
          <Menu.Item closeOnClick onClick={() => setIsDeepLinkModalOpen(true)}>
            <span>Edit in Shapr3D app</span>
          </Menu.Item>
        )}
        {onCopyLink && (
          <Menu.Item closeOnClick onClick={onCopyLink}>
            <span>Copy Link</span>
          </Menu.Item>
        )}
        {canShare && onShare && (
          <Menu.Item closeOnClick onClick={onShare}>
            <span>Share...</span>
          </Menu.Item>
        )}
        {canDownload && onDownload && (
          <Menu.Item closeOnClick onClick={onDownload}>
            <span>Download as .shapr</span>
          </Menu.Item>
        )}
      </Menu>
      <div>
        {lastModifiedAt && (
          <div className={style.lastEdited}>Last edited: {formatDate(lastModifiedAt)}</div>
        )}
        <UserName user={createdByUser} fallback={"-"} color="gray" />
      </div>
      {showEditInAppButton && (
        <Button
          variant="grayscale"
          className={style.editInAppButton}
          onClick={() => setIsDeepLinkModalOpen(true)}
        >
          <ButtonIconWithLabel icon={<Icon_OpenNew24 />} label="Edit in Shapr3D app" />
        </Button>
      )}
    </div>
  );
};
